import { reactive, markRaw } from 'vue';

// The site-wide stack of open pop-out panels. Plain reactive singleton shared by
// ES module import, same convention as modeStore.js / searchBarStore.js /
// basketStore.js - every Vue island on the page gets the same instance, so a
// component in one app root can open a panel rendered by the host in another.
//
// Vue twin of the pop-out panel system in resources/js/app.js
// (showPopOutPanel/hidePopOutPanel), but stacking: app.js identifies one panel at
// a time by its data-pop-out-panel-id, whereas this holds an ordered list and
// only the last entry is visible (see PopOutPanel.vue's `covered` prop). The two
// systems are deliberately decoupled - app.js only ever touches panels carrying
// that attribute, and Vue-rendered panels never carry it. See
// docs/blade-to-vue-components.md.
//
// Mutate this only through the functions below, so the scroll lock and the
// close-then-remove sequence cannot be bypassed.
export const stack = reactive([]);

// Incrementing rather than random so keys are stable and debuggable, and so two
// panels opened with identical content still get distinct keys - Vue needs that
// for :key, and a stack legitimately holds the same component twice.
let nextKey = 1;

function normalise(entry) {
    return {
        key: nextKey++,

        // markRaw, or Vue warns that a component was made reactive: the entry
        // itself needs to be reactive (for `closing`), but the component
        // definition inside it must not be proxied.
        component: markRaw(entry.component),

        props: entry.props ?? {},
        title: entry.title ?? null,
        titleClass: entry.titleClass ?? null,

        // Bumped by replacePanelContent(). PopOutPanel.vue watches it to reset the
        // panel's scroll position, which it otherwise keeps across a content change
        // because the scrolling element is the panel, not the content.
        version: 0,

        // Set once this panel's slide-in has finished. Until then it is not covering
        // anything, so the panel underneath must stay visible - hiding it on push
        // instead leaves the page bare for the length of the slide.
        arrived: false,

        // Set while the slide-out transition runs, before the entry is removed.
        closing: false,
    };
}

// Pushes a panel on top of whatever is already open. The one below stays mounted
// and keeps its state (scroll position, form values, in-flight requests) - it is
// only hidden, so closing this one reveals it untouched.
export function openPanel(entry) {
    const normalised = normalise(entry);
    stack.push(normalised);
    document.body.classList.add('noscroll');

    return normalised.key;
}

// Changes what an already-open panel is showing, in place. The panel element is not
// unmounted and never loses .visible, so it stays exactly where it is and only its
// body and header title change - no slide, no gap.
//
// This is the other half of the pair with openPanel(), and the two are chosen by one
// question: is there something to come back to? Going deeper, where the user will
// want the previous panel again, is openPanel(). A panel whose job has changed, with
// nothing to return to, is this - the calendar handing over to the basket after a
// successful add, where returning to the submitted form would only invite a duplicate
// basket item.
//
// Defaults to the topmost panel, which is the only visible one and therefore the one
// any button calling this is sitting in.
export function replacePanelContent(entry, key = null) {
    const target = key === null ? topmost() : stack.find(e => e.key === key);

    if (!target) {
        return null;
    }

    target.component = markRaw(entry.component);
    target.props = entry.props ?? {};
    target.title = entry.title ?? null;
    target.titleClass = entry.titleClass ?? null;
    target.version++;

    return target.key;
}

// Called by PopOutPanel.vue when its slide-in finishes. Only then is it actually
// covering the panel below, which is when that one may safely be hidden.
export function markPanelArrived(key) {
    const entry = stack.find(e => e.key === key);

    if (entry) {
        entry.arrived = true;
    }
}

// Asks a panel to close. This does not remove it: PopOutPanel.vue watches
// `closing`, drops its .visible class to run the slide-out, and calls
// finalisePanel() when the transition ends. Removing the entry here instead would
// unmount the panel mid-slide and it would simply vanish.
//
// With no key, closes the topmost panel that is not already on its way out. Skipping
// the ones already closing is what lets a run of quick clicks peel the stack back one
// panel per click, instead of every click after the first re-marking the same entry
// and appearing to do nothing.
export function closePanel(key = null) {
    const entry = key === null
        ? [...stack].reverse().find(e => !e.closing)
        : stack.find(e => e.key === key);

    if (entry) {
        entry.closing = true;
    }
}

// Removes an entry for real. Called by PopOutPanel.vue once its slide-out has
// finished, not by application code.
export function finalisePanel(key) {
    const index = stack.findIndex(e => e.key === key);

    if (index !== -1) {
        stack.splice(index, 1);
    }

    releaseBodyScrollIfLast();
}

// Closes everything. Only the top panel animates out - the rest are covered and
// therefore invisible, so there is nothing to animate. The survivor's own
// finalisePanel() releases the scroll lock for all of them.
export function closeAllPanels() {
    if (!stack.length) {
        return;
    }

    stack.splice(0, stack.length - 1);
    closePanel();
}

export function topmost() {
    return stack.length ? stack[stack.length - 1] : null;
}

// Mirrors app.js's preventBodyScroll/allowBodyScroll, which are module-local
// there and so not callable from here. The release checks for Blade-rendered
// panels as well as this stack: a Vue panel closing must not unlock the page
// while the hamburger menu is still open. That is the same guard app.js's
// hidePopOutPanel() applies for the mirror case.
function releaseBodyScrollIfLast() {
    if (stack.length || document.querySelector('.pop_out_panel.visible')) {
        return;
    }

    document.body.classList.remove('noscroll');
}

// One listener for the whole stack rather than one per panel, so Escape closes
// only the topmost. app.js closes every visible Blade panel on Escape; that
// behaviour is wrong for a stack, so this deliberately diverges.
document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') {
        closePanel();
    }
});
